// search operators, or a query that is only quoted phrases, are someone after
// precise results; a question can still quote a phrase
const SEARCH_SYNTAX =
  /^\s*(?:"[^"]+"\s*)+$|(?:^|\s)-?(?:in|status|order|before|after|with|category|categories|tags?|user|group|badge|topic|min_\w+|max_\w+):\S|(?:^|\s)[#@]\S+/i;

const QUESTION_MARK = /[?？]\s*$/;

// this many words is natural language rather than keywords
const QUESTION_LENGTH = 5;

// below this the decision model is no surer than how the query is written
const INTENT_PROBABILITY = 0.7;

// the writing systems each site language is searched in; anything else is
// left out of the search index's stemming and matches poorly
const LOCALE_SCRIPTS = {
  ar: ["Arabic"],
  be: ["Cyrillic"],
  bg: ["Cyrillic"],
  el: ["Greek"],
  fa: ["Arabic"],
  he: ["Hebrew"],
  hi: ["Devanagari"],
  ja: ["Han", "Hiragana", "Katakana"],
  kk: ["Cyrillic"],
  ko: ["Hangul", "Han"],
  mk: ["Cyrillic"],
  ru: ["Cyrillic"],
  sr: ["Cyrillic", "Latin"],
  th: ["Thai"],
  uk: ["Cyrillic"],
  ur: ["Arabic"],
  zh: ["Han"],
};
const DETECTED_SCRIPTS = [
  "Latin",
  "Cyrillic",
  "Greek",
  "Arabic",
  "Hebrew",
  "Devanagari",
  "Han",
  "Hiragana",
  "Katakana",
  "Hangul",
  "Thai",
].map((name) => [name, new RegExp(`\\p{Script=${name}}`, "u")]);
const FOREIGN_SHARE = 0.6;

/**
 * @typedef {"ask"|"topics"|"context"} SearchTab
 */

/**
 * @typedef {Object} SearchIntent
 * @property {"answer"|"lookup"} intent what a decision model judged the reader
 *   is after
 * @property {number} probability the chance the model gives that intent
 * @property {"here"|"site"} [scope] for a search made in a topic or category,
 *   whether it is about that place or the whole site
 * @property {number} [scope_probability] the chance the model gives that scope
 */

/**
 * Which tab a search should open on, guessed from how the query reads and
 * what the keyword searches found. Every tab is fetched, so this only decides
 * what the reader sees first.
 *
 * @param {string} query
 * @param {Object} found
 * @param {Object} [found.topics] the site-wide keyword results
 * @param {Object} [found.context] the keyword results within the context
 * @param {string} [found.scope] the context's scope key, when there is one
 * @param {string} [found.siteLocale] the language the site is searched in
 * @param {boolean} [found.searchedAgain] whether an earlier search on this
 *   page seems not to have helped
 * @param {SearchIntent} [found.intent] a decision model's judgement, which
 *   can open a search on the answer when it is confident, but never away from it
 * @param {boolean} [found.prefersResults] whether the reader's recent searches
 *   show them leaving the answer for the results
 * @param {boolean} [found.answerDeferred] whether the answer waits for the
 *   reader to request it
 * @returns {{tab: SearchTab, reason: string}}
 */
export function chooseTab(
  query,
  {
    topics,
    context,
    scope,
    siteLocale,
    searchedAgain,
    intent,
    prefersResults,
    answerDeferred,
  } = {}
) {
  const topicCount = topics?.posts?.length ?? 0;
  // a search about the whole site is not shown its context first
  const aboutSite =
    intent?.scope === "site" && intent.scope_probability >= INTENT_PROBABILITY;
  const contextCount = scope && !aboutSite ? (context?.posts?.length ?? 0) : 0;

  if (usesSearchSyntax(query)) {
    return {
      tab: contextCount > 0 ? "context" : "topics",
      reason: "syntax",
    };
  }

  if (answerDeferred) {
    return {
      tab: contextCount > 0 ? "context" : "topics",
      reason: "answer_deferred",
    };
  }

  // within a topic the reader is after a passage of it
  if (scope?.startsWith("topic:") && contextCount > 0) {
    return { tab: "context", reason: "topic_match" };
  }

  // keyword search does poorly outside the site's language
  if (inForeignScript(query, siteLocale)) {
    return { tab: "ask", reason: "language" };
  }

  // a second search here means the first one's results did not help
  if (searchedAgain) {
    return { tab: "ask", reason: "searched_again" };
  }

  if (prefersResults && (contextCount > 0 || topicCount > 0)) {
    return {
      tab: contextCount > 0 ? "context" : "topics",
      reason: "reader_prefers_results",
    };
  }

  // The model spots answers well but lookups poorly, and the answer usually
  // serves a reader better than keyword results, so it only ever adds answers.
  if (intent?.intent === "answer" && intent.probability >= INTENT_PROBABILITY) {
    return { tab: "ask", reason: "intent_answer" };
  }
  if (readsAsQuestion(query)) {
    return { tab: "ask", reason: "question" };
  }

  if (contextCount > 0) {
    return { tab: "context", reason: "context_match" };
  }

  if (topicCount > 0) {
    return { tab: "topics", reason: "topic_results" };
  }

  return { tab: "ask", reason: "no_keyword_matches" };
}

/**
 * Whether most of the query's letters are in a writing system the site's
 * language does not use.
 */
export function inForeignScript(query, siteLocale) {
  const expected = LOCALE_SCRIPTS[(siteLocale ?? "").split(/[_-]/)[0]] ?? [
    "Latin",
  ];
  const letters = [...(query ?? "")].filter((char) => /\p{L}/u.test(char));
  if (letters.length === 0) {
    return false;
  }

  // Latin letters are never counted: product names, code and borrowed terms
  // are written in them on sites in any language
  const foreign = letters.filter((char) => {
    const script = DETECTED_SCRIPTS.find(([, pattern]) => pattern.test(char));
    return script && script[0] !== "Latin" && !expected.includes(script[0]);
  });
  return foreign.length / letters.length >= FOREIGN_SHARE;
}

export function usesSearchSyntax(query) {
  return SEARCH_SYNTAX.test(query ?? "");
}

export function readsAsQuestion(query) {
  return QUESTION_MARK.test(query ?? "") || wordCount(query) >= QUESTION_LENGTH;
}

/**
 * Words rather than spaces, so a question in a language written without
 * spaces between words still counts as several.
 */
export function wordCount(query) {
  const text = query?.trim() ?? "";
  if (!text) {
    return 0;
  }

  if (typeof Intl?.Segmenter === "function") {
    const segments = new Intl.Segmenter(undefined, { granularity: "word" });
    return [...segments.segment(text)].filter((s) => s.isWordLike).length;
  }

  return text.split(/\s+/).length;
}
